import {ChangeDetectionStrategy, Component, computed, inject, input} from '@angular/core';
import {toSignal} from '@angular/core/rxjs-interop';
import {TranslocoPipe, TranslocoService} from '@jsverse/transloco';
import {NgxEchartsModule} from 'ngx-echarts';
import {DirectusTranslation} from '@frontend/directus';
import {TranslateDirectusPipe} from '@frontend/transloco';
import {CriteriaWeightPipe, formatCriteriaWeight} from '../criteria-weight.pipe';
import {criteriaColorToken} from '../criteria-weights-overview.utils';
import {
  CriteriaWeightsCriterion,
  CriteriaWeightsSubCriterion,
} from '../step-4-3-criteria-weights-overview.types';
import {
  CriteriaWeightsTreemapFormatterParams,
  CriteriaWeightsTreemapNode,
} from './criteria-weights-treemap.types';

const CHATBOT_CLEARANCE_RIGHT = 80;
const CHATBOT_CLEARANCE_BOTTOM = 96;

@Component({
  selector: 'app-criteria-weights-treemap',
  imports: [NgxEchartsModule, TranslocoPipe, TranslateDirectusPipe, CriteriaWeightPipe],
  templateUrl: './criteria-weights-treemap.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CriteriaWeightsTreemapComponent {
  private readonly translocoService = inject(TranslocoService);
  private readonly translateDirectusPipe = new TranslateDirectusPipe();

  readonly criteria = input.required<CriteriaWeightsCriterion[]>();
  readonly testId = input.required<string>();

  private readonly documentStyle = getComputedStyle(document.documentElement);

  private readonly activeLanguage = toSignal(this.translocoService.langChanges$, {
    initialValue: this.translocoService.getActiveLang(),
  });

  protected readonly totalWeight = computed<number | null>(() => {
    const tileWeights = this.criteria()
      .flatMap((criterion) => this.tileWeights(criterion))
      .filter((weight): weight is number => weight !== null);

    return tileWeights.length > 0 ? tileWeights.reduce((total, weight) => total + weight, 0) : null;
  });

  protected readonly chartOptions = computed(() => {
    const language = this.activeLanguage();
    const labelColor = this.cssVariable('--p-surface-900');
    const gapColor = this.cssVariable('--p-surface-900');

    return {
      animation: false,
      tooltip: {
        confine: true,
        backgroundColor: this.cssVariable('--p-surface-800'),
        borderColor: this.cssVariable('--p-surface-600'),
        textStyle: {color: this.cssVariable('--p-text-color')},
        formatter: (params: CriteriaWeightsTreemapFormatterParams) =>
          this.formatTooltip(params.data),
      },
      series: [
        {
          type: 'treemap',
          left: 0,
          top: 0,
          right: CHATBOT_CLEARANCE_RIGHT,
          bottom: CHATBOT_CLEARANCE_BOTTOM,
          roam: false,
          nodeClick: false,
          breadcrumb: {show: false},
          itemStyle: {color: gapColor, borderColor: gapColor, borderWidth: 0},
          label: {
            show: true,
            position: 'insideTopLeft',
            overflow: 'truncate',
            lineOverflow: 'truncate',
            formatter: (params: CriteriaWeightsTreemapFormatterParams) =>
              `{identifier|${params.data.name}}\n{share|${params.data.shareLabel}}`,
            rich: {
              identifier: {color: labelColor, fontWeight: 'bold', fontSize: 11, lineHeight: 14},
              share: {color: labelColor, fontSize: 11, lineHeight: 13},
            },
          },
          levels: [
            {itemStyle: {color: gapColor, borderWidth: 0, gapWidth: 6}},
            {itemStyle: {borderWidth: 0, gapWidth: 4, borderRadius: 8}},
            {itemStyle: {borderWidth: 0, gapWidth: 0, borderRadius: 6}},
          ],
          data: this.criteria().map((criterion) =>
            this.toCriterionNode(criterion, language, gapColor),
          ),
        },
      ],
    };
  });

  private tileWeights(criterion: CriteriaWeightsCriterion): (number | null)[] {
    return criterion.subCriteria.length > 0
      ? criterion.subCriteria.map((subCriterion) => subCriterion.overallWeight)
      : [criterion.weight];
  }

  private toCriterionNode(
    criterion: CriteriaWeightsCriterion,
    language: string,
    gapColor: string,
  ): CriteriaWeightsTreemapNode {
    const color = this.cssVariable(criteriaColorToken(criterion.ecNumber));
    const name = this.translateName(criterion.translations, language);
    const children = criterion.subCriteria.map((subCriterion) =>
      this.toSubCriterionNode(subCriterion, color, language, gapColor),
    );
    const groupColor = children.length > 0 ? gapColor : color;

    return {
      name: criterion.identifier,
      value:
        children.length > 0
          ? children.reduce((total, child) => total + child.value, 0)
          : (criterion.weight ?? 0),
      shareLabel: this.shareLabel(criterion.weight),
      title: `${criterion.identifier} · ${name}`,
      weight: criterion.weight,
      overallWeight: criterion.weight,
      itemStyle: {color: groupColor, borderColor: gapColor},
      ...(children.length > 0 && {children}),
    };
  }

  private toSubCriterionNode(
    subCriterion: CriteriaWeightsSubCriterion,
    color: string,
    language: string,
    gapColor: string,
  ): CriteriaWeightsTreemapNode {
    const name = this.translateName(subCriterion.translations, language);

    return {
      name: subCriterion.identifier,
      value: subCriterion.overallWeight ?? 0,
      shareLabel: this.shareLabel(subCriterion.overallWeight),
      title: `${subCriterion.identifier} · ${name}`,
      weight: subCriterion.weight,
      overallWeight: subCriterion.overallWeight,
      itemStyle: {color, borderColor: gapColor},
    };
  }

  private shareLabel(weight: number | null): string {
    return `${formatCriteriaWeight(weight)} ${this.translocoService.translate('project.steps.criteriaWeightsOverview.influence')}`;
  }

  private formatTooltip(node: CriteriaWeightsTreemapNode): string {
    const weightLabel = this.translocoService.translate(
      'project.steps.criteriaWeightsOverview.weight',
    );
    const overallWeightLabel = this.translocoService.translate(
      'project.steps.criteriaWeightsOverview.overallWeight',
    );

    return `
      <div style="font-weight: bold; margin-bottom: 6px;">${this.escapeHtml(node.title)}</div>
      <div>${weightLabel}: ${formatCriteriaWeight(node.weight)}</div>
      <div>${overallWeightLabel}: ${formatCriteriaWeight(node.overallWeight)}</div>
    `;
  }

  private translateName(translations: DirectusTranslation[], language: string): string {
    return this.translateDirectusPipe.transform(translations, 'name', false, language) ?? '';
  }

  private cssVariable(name: string): string {
    return this.documentStyle.getPropertyValue(name).trim();
  }

  private escapeHtml(text: string): string {
    return text
      .replaceAll('&', '&amp;')
      .replaceAll('<', '&lt;')
      .replaceAll('>', '&gt;')
      .replaceAll('"', '&quot;');
  }
}
